import { format } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';
import { Card, CollapseGroup, Eyebrow, Skeleton, Subhead } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab,
  CoachConversation,
  NudgeList,
  NudgeSchemaTrigger,
  useSetActiveClientInfoDrawerTab,
  useSetCoachConversation,
} from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { Maybe } from '../../types';
import { useOpenClientInfoDrawerForAppointment } from '../client-information/useOpenClientInfoDrawerForAppointment';
import { createNudgeOnClickHandlers, getProviderDashboardReferralUri } from '../client-information/utils';
import { drawerStateAtom, providerShortIdAtom } from '../state';
import { useMomentumPilotAccess } from '../useMomentumPilotAccess';
import { ActivitySectionContent } from './ActivitySectionContent';
import { FocusOfLastSessionContent } from './FocusOfLastSession';
import { IntentionCheckInsContent } from './IntentionCheckInsContent';
import { IntersessionSummaryDisclaimer } from './IntersessionSummaryDisclaimer';
import { MeasuresSectionContent } from './MeasuresSectionContent';
import { NoUpcomingAppointmentsCard } from './NoUpcomingAppointmentsCard';
import {
  GetIntention__CurrentSessionIntention,
  IntersessionSummary__Appointment,
  IntersessionSummary__Patient,
} from './types';
import { useFormatContentData } from './useFormatContentData';
import { useFormatFocusOfLastSessionData } from './useFormatFocusOfLastSessionData';
import { useGetCoachAccess } from './useGetCoachAccess';
import { useGetIntentionQuery } from './useGetIntentionQuery';
import { useIntersessionSummary } from './useGetIntersessionSummary';
import { useGetOutstandingMeasures } from './useGetOutstandingMeasures';
import { formatBetweenSessionAction, formatFollowupHeader, formatIntakeHeader, formatMeasureTitle } from './utils';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

interface IntersessionSummaryProps {
  appointment: IntersessionSummary__Appointment;
  patient: IntersessionSummary__Patient;
  openClientInfoDrawerForAppointment: (tab: ClientInformationDrawerTab) => void;
  onClickUnreadCoachConversation: (conversation: CoachConversation) => void;
  onNudgeClickHandlers?: Partial<Record<NudgeSchemaTrigger, () => void>>;
  providerShortId: string;
  hasCoachAccess: boolean;
  hasOutstandingMeasures: boolean;
  onViewMeasuresClick: () => void;
  onSectionToggle: (sectionName: 'last_session' | 'client_activity' | 'measures', isExpanded: boolean) => void;
  currentIntention?: Maybe<GetIntention__CurrentSessionIntention>;
}

export function IntersessionSummary({
  appointment,
  patient,
  openClientInfoDrawerForAppointment,
  onClickUnreadCoachConversation,
  onNudgeClickHandlers,
  providerShortId,
  hasCoachAccess,
  hasOutstandingMeasures,
  onViewMeasuresClick,
  onSectionToggle,
  currentIntention,
}: IntersessionSummaryProps): JSX.Element {
  const t = useTranslate();
  const content = appointment.preVisitSummary?.content;
  const pronoun = patient.pronouns?.split('/')[0] ?? 'they';

  const { isFollowUp, coachTopics, completedResources, reflectionTopics, numCompletedResources } =
    useFormatContentData(content);

  const [isLastSessionSectionOpen, setIsLastSessionSectionOpen] = useState(false);
  const [isActivitySectionOpen, setIsActivitySectionOpen] = useState(false);
  const [isMeasuresSectionOpen, setIsMeasuresSectionOpen] = useState(false);

  const focusOfLastSessionData = useFormatFocusOfLastSessionData(content);
  const formattedDate = useMemo(
    () => format(new Date(appointment.timeStart), 'MMMM d, yyyy – h:mm a'),
    [appointment.timeStart],
  );

  const handleLastSessionToggle = useCallback(
    (isExpanded: boolean) => {
      setIsLastSessionSectionOpen(isExpanded);
      onSectionToggle('last_session', isExpanded);
    },
    [onSectionToggle],
  );

  const handleActivityToggle = useCallback(
    (isExpanded: boolean) => {
      setIsActivitySectionOpen(isExpanded);
      onSectionToggle('client_activity', isExpanded);
    },
    [onSectionToggle],
  );

  const handleMeasuresToggle = useCallback(
    (isExpanded: boolean) => {
      setIsMeasuresSectionOpen(isExpanded);
      onSectionToggle('measures', isExpanded);
    },
    [onSectionToggle],
  );

  return (
    <Card
      title={patient.preferredFullName}
      description={formattedDate}
      headerClassName='sticky top-0 rebrand:bg-surface-default bg-neutral-200 pt-8 z-10 rebrand:mb-250'
      className='max-w-3xl overflow-y-auto mb-8 rebrand:rounded-425-450 rebrand:p-450 sm:pt-0 pt-0 rebrand:pt-0 isolate'
    >
      {(patient?.unreadSafetyConcernConversation || patient?.nudges) && (
        <div className='rebrand:mb-250'>
          <NudgeList
            nudges={patient.nudges ?? []}
            onClickHandlers={onNudgeClickHandlers}
            context='telehealth-waiting-room'
            unreadSafetyConcernConversation={patient.unreadSafetyConcernConversation ?? undefined}
            onClickUnreadConversation={onClickUnreadCoachConversation}
          />
        </div>
      )}
      <Subhead.Default>
        {isFollowUp
          ? formatFollowupHeader(t, patient.firstName, pronoun, coachTopics, numCompletedResources, reflectionTopics)
          : formatIntakeHeader(t, patient.firstName, coachTopics)}
      </Subhead.Default>
      {!!currentIntention && (
        <div className='flex flex-col mt-450 pb-250 gap-200 border-b border-border-default'>
          <div>
            <Eyebrow.Large className='text-xs font-medium uppercase text-neutral-700 rebrand:text-text-muted'>
              {t('telehealth-core:providerIntersessionSummary.intentionCheckIns')}
            </Eyebrow.Large>
            <Subhead.Small className='py-150 break-words'>{currentIntention.contentText}</Subhead.Small>
          </div>
          {currentIntention.checkIns && currentIntention.checkIns.length > 0 && (
            <IntentionCheckInsContent checkIns={currentIntention.checkIns} />
          )}
        </div>
      )}
      <CollapseGroup
        items={[
          ...(isFollowUp
            ? [
                {
                  children: (
                    <FocusOfLastSessionContent
                      hasNotes={focusOfLastSessionData.hasNotes}
                      focusText={focusOfLastSessionData.focusText}
                      onClick={() => openClientInfoDrawerForAppointment(ClientInformationDrawerTab.INFORMATION)}
                    />
                  ),
                  eyebrowText: t('telehealth-core:providerIntersessionSummary.lastSession'),
                  title: <Subhead.Small>{focusOfLastSessionData.title}</Subhead.Small>,
                  className: 'rebrand:pl-0',
                  expanded: isLastSessionSectionOpen,
                  onToggle: handleLastSessionToggle,
                  contentClassName: 'rebrand:mt-350',
                },
              ]
            : []),
          ...(isFollowUp || coachTopics.length > 0
            ? [
                {
                  children: (
                    <ActivitySectionContent
                      hasCoachAccess={hasCoachAccess}
                      coachTopics={coachTopics}
                      messagesSent={content?.coachConversationCount ?? 0}
                      completedResources={completedResources}
                      onClick={() => openClientInfoDrawerForAppointment(ClientInformationDrawerTab.CLIENT_ACTIVITY)}
                    />
                  ),
                  eyebrowText: isFollowUp
                    ? t('telehealth-core:providerIntersessionSummary.activitySinceLastSession')
                    : t('telehealth-core:providerIntersessionSummary.clientActivity'),
                  title: (
                    <Subhead.Small>
                      {formatBetweenSessionAction(t, hasCoachAccess, coachTopics, numCompletedResources)}
                    </Subhead.Small>
                  ),
                  className: 'rebrand:pl-0',
                  expanded: isActivitySectionOpen,
                  onToggle: handleActivityToggle,
                  contentClassName: 'rebrand:mt-350',
                },
              ]
            : []),
          {
            children: (
              <MeasuresSectionContent
                hasCompletedMeasureResponses={patient.hasCompletedMeasureResponses}
                clientUserShortId={patient.clientUser.shortId}
                providerShortId={providerShortId}
                onClick={onViewMeasuresClick}
              />
            ),
            eyebrowText: t('telehealth-core:providerIntersessionSummary.measures'),
            title: <Subhead.Small>{formatMeasureTitle(t, hasOutstandingMeasures, content)}</Subhead.Small>,
            className: 'rebrand:pl-0',
            expanded: isMeasuresSectionOpen,
            onToggle: handleMeasuresToggle,
          },
        ]}
        variant='default'
        className='flex flex-col rebrand:gap-250 rebrand:my-250 my-4'
      />
      {appointment.preVisitSummary && (
        <IntersessionSummaryDisclaimer createdAt={appointment.preVisitSummary?.createdAt} />
      )}
    </Card>
  );
}

export function IntersessionSummaryConnected(): JSX.Element {
  const { appointment, loading } = useIntersessionSummary();
  const providerShortId = useAtomValue(providerShortIdAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);

  const { pilotAccess: hasIntentionsInPVSAccess, loading: isLoadingPilotAccess } = useMomentumPilotAccess(
    providerShortId ?? '',
    'intentions_in_pvs',
  );
  const clientUserShortId = appointment?.patient?.clientUser?.shortId ?? '';

  const { coachReleasedToClient, loading: isCoachLoading } = useGetCoachAccess(clientUserShortId, {
    skip: !clientUserShortId,
  });
  const { outstandingMeasures, loading: isLoadingOutstandingMeasures } = useGetOutstandingMeasures(
    clientUserShortId,
    providerShortId ?? '',
  );
  const { currentIntention, loading: isLoadingIntention } = useGetIntentionQuery(
    clientUserShortId,
    providerShortId ?? '',
  );

  const { setCoachConversation } = useSetCoachConversation();
  const { userClicked, userViewed } = useTrackEvent();
  const openClientInfoDrawerForAppointment = useOpenClientInfoDrawerForAppointment();
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();

  const isLoading =
    loading || isLoadingPilotAccess || isCoachLoading || isLoadingOutstandingMeasures || isLoadingIntention;
  const hasOutstandingMeasures = useMemo(
    () => outstandingMeasures && outstandingMeasures.length > 0,
    [outstandingMeasures],
  );
  const intersessionMeasuresTrackingProperties = useMemo(
    () =>
      appointment?.patient && providerShortId
        ? {
            appointmentShortId: appointment.appointmentShortId,
            patientShortId: appointment.patient.shortId,
            providerShortId,
            hasCompletedMeasureResponses: appointment.patient.hasCompletedMeasureResponses,
            hasOutstandingMeasures,
          }
        : undefined,
    [appointment, hasOutstandingMeasures, providerShortId],
  );

  useEffect(
    function trackIntersessionSummaryViewed() {
      if (appointment?.patient && intersessionMeasuresTrackingProperties && !isLoading) {
        userViewed(EventTypeEnum.INTERSESSION_SUMMARY_VIEWED, intersessionMeasuresTrackingProperties);

        // Track NudgeList view if nudges or safety concerns are present
        if (appointment.patient?.nudges || appointment.patient?.unreadSafetyConcernConversation) {
          userViewed(EventTypeEnum.NUDGE_LIST_VIEWED, {
            appointmentShortId: appointment.appointmentShortId,
            source: 'telehealth-waiting-room',
          });
        }
      }
    },
    [isLoading, appointment, intersessionMeasuresTrackingProperties, userViewed],
  );

  const handleOpenDrawer = useCallback(
    (tab: ClientInformationDrawerTab) => {
      if (appointment) {
        openClientInfoDrawerForAppointment(appointment);
        setSeedlingActiveTab(tab);
      }
    },
    [appointment, openClientInfoDrawerForAppointment, setSeedlingActiveTab],
  );

  const handleViewMeasures = useCallback(() => {
    if (intersessionMeasuresTrackingProperties) {
      userClicked(EventTypeEnum.INTERSESSION_VIEW_MEASURES_BUTTON_CLICKED, intersessionMeasuresTrackingProperties);
    }
    handleOpenDrawer(ClientInformationDrawerTab.TREATMENT);
  }, [handleOpenDrawer, intersessionMeasuresTrackingProperties, userClicked]);

  const handleSectionToggle = useCallback(
    (sectionName: 'last_session' | 'client_activity' | 'measures', isExpanded: boolean) => {
      if (!intersessionMeasuresTrackingProperties) return;

      userClicked(EventTypeEnum.INTERSESSION_SECTION_TOGGLED, {
        ...intersessionMeasuresTrackingProperties,
        sectionName,
        isExpanded,
      });
    },
    [intersessionMeasuresTrackingProperties, userClicked],
  );

  const onClickUnreadCoachConversation = useCallback(
    (conversation: CoachConversation) => {
      if (appointment && conversation.id) {
        openClientInfoDrawerForAppointment(appointment);
        setSeedlingActiveTab(ClientInformationDrawerTab.CLIENT_ACTIVITY);
        setCoachConversation(conversation);

        // Track safety concern conversation click
        userClicked(EventTypeEnum.SAFETY_CONCERN_CONVERSATION_CLICKED, {
          appointmentShortId: appointment.appointmentShortId,
          source: 'telehealth-waiting-room',
          conversationUuid: conversation.uuid,
        });
      }
    },
    [appointment, openClientInfoDrawerForAppointment, setSeedlingActiveTab, setCoachConversation, userClicked],
  );

  const onNudgeClickHandlers = createNudgeOnClickHandlers(
    appointment?.patient?.nudges ?? [],
    setDrawerState,
    getProviderDashboardReferralUri(appointment?.patient?.id ?? ''),
    userClicked,
    false,
    appointment?.appointmentShortId,
    'telehealth-waiting-room',
  );

  if (isLoading) {
    return (
      <Card className='max-h-[80%] sm:w-[40rem] w-full rebrand:p-450 rebrand:rounded-425-450'>
        <Skeleton />
      </Card>
    );
  }

  if (!appointment || !providerShortId || !appointment.patient) {
    return <NoUpcomingAppointmentsCard />;
  }

  return (
    <ErrorBoundary>
      <IntersessionSummary
        appointment={appointment}
        patient={appointment.patient}
        openClientInfoDrawerForAppointment={handleOpenDrawer}
        onClickUnreadCoachConversation={onClickUnreadCoachConversation}
        onSectionToggle={handleSectionToggle}
        onNudgeClickHandlers={onNudgeClickHandlers}
        providerShortId={providerShortId}
        hasCoachAccess={coachReleasedToClient ?? false}
        hasOutstandingMeasures={hasOutstandingMeasures}
        onViewMeasuresClick={handleViewMeasures}
        currentIntention={hasIntentionsInPVSAccess?.enabled ? currentIntention : undefined}
      />
    </ErrorBoundary>
  );
}
